"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Lock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const defaultServiceTypes = [{
  value: "appliance",
  label: "Appliance"
}, {
  value: "hvac",
  label: "HVAC"
}, {
  value: "plumbing",
  label: "Plumbing"
}, {
  value: "electrical",
  label: "Electrical"
}, {
  value: "maintenance",
  label: "Maintenance"
}];
const infoLines = [{
  label: "Response",
  value: "Quick local matchmaking"
}, {
  label: "Visit",
  value: "Pro arrival 1-2 days"
}, {
  label: "Quote",
  value: "On-site assessment"
}];
function InquiryForm() {
  const [form, setForm] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const fetchTimeRef = useRef<number>(0);
  const [values, setValues] = useState({
    full_name: "",
    email: "",
    phone: "",
    service_address: "",
    service_type: "",
    message: ""
  });
  const fetchFormDefinition = () => {
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadError("error");
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request").then((res: any) => {
      setForm(res);
      fetchTimeRef.current = Date.now();
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listRes: any) => {
          const items = listRes?.items || [];
          const matched = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => f.name?.toLowerCase().includes("service") || f.name?.toLowerCase().includes("homeowner"));
          if (matched) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`).then((subRes: any) => {
              setForm(subRes);
              fetchTimeRef.current = Date.now();
            });
          } else {
            setLoadError("missing");
          }
        }).catch(() => setLoadError("error"));
      } else {
        setLoadError("error");
      }
    });
  };
  useEffect(() => {
    fetchFormDefinition();
  }, []);
  const fieldMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (form?.fields) {
      for (const f of form.fields) {
        map[f.key] = f;
      }
    }
    return map;
  }, [form]);
  const serviceOptions = useMemo(() => {
    const serviceField = fieldMap["service_type"];
    if (serviceField?.options && serviceField.options.length > 0) {
      return serviceField.options;
    }
    return defaultServiceTypes;
  }, [fieldMap]);
  const handleChange = (field: string, val: string) => {
    setValues(prev => ({
      ...prev,
      [field]: val
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const resetForm = () => {
    setValues({
      full_name: "",
      email: "",
      phone: "",
      service_address: "",
      service_type: "",
      message: ""
    });
    setFieldErrors({});
    setSubmitError(null);
    setIsSubmitted(false);
    setHpValue("");
    fetchFormDefinition();
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setIsSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});
    try {
      const minSeconds = form.spam?.min_seconds ?? 3;
      const elapsed = (Date.now() - fetchTimeRef.current) / 1000;
      if (elapsed < minSeconds) {
        await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsed) * 1000 + 100));
      }
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        throw new Error("Client unavailable");
      }
      const valuesToSend: Record<string, any> = {};
      const assignVal = (key: string, val: any) => {
        const f = fieldMap[key];
        if (f?.id) {
          valuesToSend[f.id] = val;
        }
      };
      assignVal("full_name", values.full_name);
      assignVal("email", values.email);
      assignVal("phone", values.phone);
      assignVal("service_address", values.service_address);
      assignVal("service_type", values.service_type);
      assignVal("message", values.message);
      assignVal("appliance_type", "");
      assignVal("source_page", "Home Maintenance");
      assignVal("source_city", "");
      assignVal("source_service", "Home Maintenance");
      const hpData = form.spam?.honeypot?.field ? {
        [form.spam.honeypot.field]: hpValue
      } : undefined;
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${form.key}/submit`, {
        method: "POST",
        data: {
          values: valuesToSend,
          token: form.spam.token,
          hp: hpData,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        setIsSubmitted(true);
      } else if (res.code === "frms_validation_failed") {
        const mappedErrors: Record<string, string> = {};
        if (res.errors) {
          for (const [id, errObj] of Object.entries(res.errors as Record<string, any>)) {
            const matchedField = form.fields?.find((f: any) => f.id === id);
            if (matchedField?.key) {
              mappedErrors[matchedField.key] = errObj.message;
            } else {
              mappedErrors[id] = errObj.message;
            }
          }
        }
        setFieldErrors(mappedErrors);
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else {
        setSubmitError(res.message || "Error sending request. Please try again.");
      }
    } catch {
      setSubmitError("Error sending request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="border-l-2 border-primary pl-6 py-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
          Request sent
        </p>
        <h3 className="font-mono uppercase text-3xl md:text-4xl font-bold leading-[0.98] text-foreground mb-6">
          Request received — a pro will contact you.
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          A local contractor will review your request and contact you shortly to confirm details.
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="rounded-lg border-foreground text-foreground hover:bg-foreground hover:text-background">
          New request
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6">
      {form?.spam?.honeypot?.field && <input type="text" name={form.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true" />}

      {loadError && <div className="p-4 border border-destructive/40 bg-destructive/5">
          <p className="text-destructive text-sm">
            Unable to load form definition. Please reach out to support.
          </p>
        </div>}

      <div className="grid gap-2">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Full Name
        </label>
        <Input placeholder="Jane Smith" value={values.full_name} onChange={e => handleChange("full_name", e.target.value)} className="border-0 border-b border-muted-foreground bg-transparent px-3 py-3 h-auto focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground" />
        {fieldErrors.full_name && <p className="text-destructive text-sm">{fieldErrors.full_name}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="grid gap-2">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Email
          </label>
          <Input type="email" placeholder="jane@email.com" value={values.email} onChange={e => handleChange("email", e.target.value)} className="border-0 border-b border-muted-foreground bg-transparent px-3 py-3 h-auto focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground" />
          {fieldErrors.email && <p className="text-destructive text-sm">{fieldErrors.email}</p>}
        </div>
        <div className="grid gap-2">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Phone
          </label>
          <Input type="tel" placeholder="(555) 000-0000" value={values.phone} onChange={e => handleChange("phone", e.target.value)} className="border-0 border-b border-muted-foreground bg-transparent px-3 py-3 h-auto focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground" />
          {fieldErrors.phone && <p className="text-destructive text-sm">{fieldErrors.phone}</p>}
        </div>
      </div>

      <div className="grid gap-2">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Service Address
        </label>
        <Input placeholder="Street address, city, state, ZIP" value={values.service_address} onChange={e => handleChange("service_address", e.target.value)} className="border-0 border-b border-muted-foreground bg-transparent px-3 py-3 h-auto focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground" />
        {fieldErrors.service_address && <p className="text-destructive text-sm">{fieldErrors.service_address}</p>}
      </div>

      <div className="grid gap-2">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Service Needed
        </label>
        <div className="flex flex-wrap gap-2 pt-2">
          {serviceOptions.map((scope: any, i: number) => {
          const active = values.service_type === scope.value;
          return <Button key={scope.value} data-index={i} type="button" onClick={() => handleChange("service_type", scope.value)} variant={active ? "default" : "outline"} className={`rounded-full px-5 py-2.5 h-auto text-sm font-medium ${active ? "bg-primary text-primary-foreground" : "border-muted-foreground text-foreground"}`}>
                {scope.label}
              </Button>;
        })}
        </div>
        {fieldErrors.service_type && <p className="text-destructive text-sm">{fieldErrors.service_type}</p>}
      </div>

      <div className="grid gap-2">
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Describe the Problem
        </label>
        <Textarea rows={5} placeholder="Tell us what your AC, HVAC system, or appliance is doing..." value={values.message} onChange={e => handleChange("message", e.target.value)} className="border-0 border-b border-muted-foreground bg-transparent px-3 py-3 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 text-base text-foreground resize-none min-h-[140px]" />
        {fieldErrors.message && <p className="text-destructive text-sm">{fieldErrors.message}</p>}
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/5">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-6">
        <Button type="submit" disabled={isSubmitting || !form} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 h-auto text-sm font-semibold tracking-wide w-full md:w-auto">
          {isSubmitting ? "Sending..." : "Request Service"}
        </Button>
        <div className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground">
          <Lock className="size-3.5" />
          <span>Secure &amp; private.</span>
        </div>
      </div>
    </form>;
}
export default function Booking() {
  const reduced = useReducedMotion();
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: reduced ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <section data-section-id="24736" id="bookingrequest" data-nav="light" className="relative bg-background py-24 md:py-44">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.div initial="hidden" whileInView="show" viewport={{
              once: true,
              amount: 0.3
            }} variants={{
              show: {
                transition: {
                  staggerChildren: 0.12
                }
              }
            }}>
                <motion.div variants={headerVariants} transition={{
                duration: 0.5,
                ease: "easeOut"
              }} className="flex items-center gap-2 mb-6">
                  <span aria-hidden="true" className="inline-block w-[1.5px] h-[10px] bg-accent-foreground" />
                  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-foreground">
                    Service Request
                  </span>
                  <span aria-hidden="true" className="inline-block w-[1.5px] h-[10px] bg-accent-foreground" />
                </motion.div>

                <motion.h2 variants={headerVariants} transition={{
                duration: 0.6,
                ease: "easeOut"
              }} className="font-mono uppercase font-bold text-foreground leading-[0.98] tracking-tight" style={{
                fontSize: "clamp(2.25rem, 4.5vw, 4.25rem)"
              }}>
                  Connect with an Atlanta pro today.
                </motion.h2>

                <motion.p variants={headerVariants} transition={{
                duration: 0.55,
                ease: "easeOut"
              }} className="mt-8 text-muted-foreground text-lg leading-[1.55] max-w-[40ch]">
                  Tell us what you need, and we will match you with a verified
                  local contractor ready to help.
                </motion.p>

                <motion.dl variants={headerVariants} transition={{
                duration: 0.55,
                ease: "easeOut"
              }} className="mt-10 space-y-2">
                  {infoLines.map((line, i) => <div key={line.label} data-index={i} className="flex items-baseline gap-3 text-sm font-medium tracking-wide text-muted-foreground leading-[1.9]">
                      <dt className="text-foreground/80">{line.label}</dt>
                      <span aria-hidden="true" className="text-muted-foreground/50">
                        ·
                      </span>
                      <dd>{line.value}</dd>
                    </div>)}
                </motion.dl>

                <motion.div variants={headerVariants} transition={{
                duration: 0.55,
                ease: "easeOut"
              }} className="mt-10 pt-8 border-t border-border">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Call support
                  </p>
                  <div className="flex items-center gap-3">
                    <Phone className="size-5 text-primary" />
                    <a href="tel:+14708479076" className="font-mono uppercase text-2xl md:text-3xl font-semibold text-foreground hover:text-primary transition-colors">
                      (470) 847-9076
                    </a>
                  </div>
                  <p className="mt-2 text-xs font-medium tracking-wide text-muted-foreground">
                    Ask your pro about availability
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <InquiryForm />
          </div>
        </div>
      </div>
    </section>;
}